import React, { useState } from "react";
import {
  DragDropContext,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import KanbanColumn from "../../Componenets/KanbanView/KanbanColumn";

export enum LeadStatusEnum {
  NEW_LEAD = "new_lead",
  TRIAL_SCHEDULED = "trial_scheduled",
  TRIAL_COMPLETED = "trial_completed",
  FOLLOWUP_PENDING = "followup_pending",
  FOLLOWUP_DONE = "followup_done",
  CONVERTED = "converted",
  NOT_CONVERTED = "not_converted",
}

interface LeadProps {
  LeadData: any[];
  isLoading?: boolean;
}

const STATUS_LABELS: Record<LeadStatusEnum, string> = {
  [LeadStatusEnum.NEW_LEAD]: "New Lead",
  [LeadStatusEnum.TRIAL_SCHEDULED]: "Trial Scheduled",
  [LeadStatusEnum.TRIAL_COMPLETED]: "Trial Completed",
  [LeadStatusEnum.FOLLOWUP_PENDING]: "Follow-up Pending",
  [LeadStatusEnum.FOLLOWUP_DONE]: "Follow-up Done",
  [LeadStatusEnum.CONVERTED]: "Converted",
  [LeadStatusEnum.NOT_CONVERTED]: "Not Converted",
};

const STATUS_COLORS: Record<LeadStatusEnum, string> = {
  [LeadStatusEnum.NEW_LEAD]: "#ff9800",
  [LeadStatusEnum.TRIAL_SCHEDULED]: "#2196f3",
  [LeadStatusEnum.TRIAL_COMPLETED]: "#673ab7",
  [LeadStatusEnum.FOLLOWUP_PENDING]: "#f06292",
  [LeadStatusEnum.FOLLOWUP_DONE]: "#ec407a",
  [LeadStatusEnum.CONVERTED]: "#4caf50",
  [LeadStatusEnum.NOT_CONVERTED]: "#f44336",
};

const getTimeAgo = (dateString: string) => {
  const now = new Date();
  const past = new Date(dateString);
  const seconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  const intervals: [number, string][] = [
    [31536000, "year"],
    [2592000, "month"],
    [604800, "week"],
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
    [1, "second"],
  ];

  for (const [secs, label] of intervals) {
    const interval = Math.floor(seconds / secs);
    if (interval >= 1) {
      return `${interval} ${label}${interval > 1 ? "s" : ""} ago`;
    }
  }
  return "just now";
};

const KanbanBoard: React.FC<LeadProps> = ({ LeadData = [], isLoading }) => {
  const [columns, setColumns] = useState(() =>
    Object.values(LeadStatusEnum).reduce((acc, status) => {
      acc[status] = {
        title: STATUS_LABELS[status],
        color: STATUS_COLORS[status],
        items: LeadData.filter(
          (lead) => lead.leadstatus?.toLowerCase() === status
        ).map((lead) => ({
          id: lead.id,
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          level: lead.year_group?.year_group || "-",
          status: STATUS_LABELS[status],
          time: getTimeAgo(lead.created_on),
        })),
      };
      return acc;
    }, {} as Record<LeadStatusEnum, { title: string; color: string; items: any[] }>)
  );

  if (isLoading) {
    return <div style={{ textAlign: "center", padding: "20px" }}>Loading...</div>;
  }

  // ✅ Handle drag end
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    // same column reorder
    if (source.droppableId === destination.droppableId) {
      const col = columns[source.droppableId as LeadStatusEnum];
      const newItems = Array.from(col.items);
      const [moved] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, moved);

      setColumns({
        ...columns,
        [source.droppableId]: { ...col, items: newItems },
      });
    } else {
      // move between columns
      const sourceCol = columns[source.droppableId as LeadStatusEnum];
      const destCol = columns[destination.droppableId as LeadStatusEnum];
      const sourceItems = Array.from(sourceCol.items);
      const destItems = Array.from(destCol.items);
      const [moved] = sourceItems.splice(source.index, 1);
      destItems.splice(destination.index, 0, moved);

      setColumns({
        ...columns,
        [source.droppableId]: { ...sourceCol, items: sourceItems },
        [destination.droppableId]: { ...destCol, items: destItems },
      });

      // ⚡ Optional: call API to update lead status here
      console.log(
        `Lead ${moved.name} moved from ${source.droppableId} → ${destination.droppableId}`
      );
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div style={{ display: "flex", gap: "16px", overflowX: "auto" }}>
        {Object.entries(columns).map(([status, column]) => (
          <Droppable droppableId={status} key={status}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ flex: "1 0 375px", minWidth: 375 }}
              >
                <KanbanColumn
                  title={column.title}
                  color={column.color}
                  items={column.items}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
};

export default KanbanBoard;
